<template>
  <div class="resource-list">
    <!-- Filtres -->
    <div class="filters">
      <input v-model="filters.search" type="text" :placeholder="$t('bo.common.search')" class="input-search" @input="debouncedLoad" />
      <select v-model="filters.status" class="select-filter" @change="loadResources()">
        <option value="">{{ $t('bo.resources.allStatuses') }}</option>
        <option value="draft">{{ $t('bo.resources.statusDraft') }}</option>
        <option value="published">{{ $t('bo.resources.statusPublished') }}</option>
        <option value="archived">{{ $t('bo.resources.statusArchived') }}</option>
      </select>
      <select v-model="filters.type" class="select-filter" @change="loadResources()">
        <option value="">{{ $t('bo.resources.allTypes') }}</option>
        <option v-for="tp in TYPES" :key="tp.value" :value="tp.value">{{ tp.label }}</option>
      </select>
      <select v-model="filters.is_global" class="select-filter" @change="loadResources()">
        <option value="">{{ $t('bo.resources.allScopes') }}</option>
        <option value="true">{{ $t('bo.resources.scopeGlobal') }}</option>
        <option value="false">{{ $t('bo.resources.scopeCompany') }}</option>
      </select>
      <select v-model="filters.availability" class="select-filter" @change="loadResources()">
        <option value="">{{ $t('bo.resources.allAvailabilities') }}</option>
        <option value="generic">{{ $t('bo.resources.availabilityGeneric') }}</option>
        <option value="program_specific">{{ $t('bo.resources.availabilityProgramSpecific') }}</option>
      </select>
    </div>

    <!-- États -->
    <div v-if="loading" class="state-msg">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="error" class="state-msg error">{{ error }}</div>
    <div v-else-if="resources.length === 0" class="state-msg empty">
      <div class="empty-icon">📭</div>
      <div>{{ $t('bo.resources.noResources') }}</div>
    </div>

    <template v-else>
      <div class="list-meta">{{ $t('bo.resources.totalCount', { count: pagination.total }) }}</div>

      <table class="table">
        <thead>
          <tr>
            <th>{{ $t('bo.resources.colTitle') }}</th>
            <th>{{ $t('bo.resources.colType') }}</th>
            <th>{{ $t('bo.resources.colStatus') }}</th>
            <th>{{ $t('bo.resources.colAudience') }}</th>
            <th>{{ $t('bo.resources.colAvailability') }}</th>
            <th>{{ $t('bo.resources.colScope') }}</th>
            <th>{{ $t('bo.resources.colSubmission') }}</th>
            <th>{{ $t('bo.resources.colUpdated') }}</th>
            <th>{{ $t('bo.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in resources" :key="r.id">
            <td class="col-title">
              <div class="res-title">{{ r.title }}</div>
              <div v-if="r.submitted_by_company?.name" class="res-company">{{ r.submitted_by_company.name }}</div>
            </td>
            <td><span class="badge-type">{{ typeLabel(r.type) }}</span></td>
            <td><span :class="['badge-status', `st-${r.status}`]">{{ statusLabel(r.status) }}</span></td>
            <td>{{ audienceLabel(r.target_audience) }}</td>
            <td>
              <span :class="['tag-availability', (r.availability || 'generic') === 'generic' ? 'av-generic' : 'av-program']">
                {{ availabilityLabel(r.availability) }}
              </span>
            </td>
            <td>
              <span :class="r.is_global ? 'tag-global' : 'tag-company'">
                {{ r.is_global ? $t('bo.resources.scopeGlobal') : $t('bo.resources.scopeCompany') }}
              </span>
            </td>
            <td>
              <span v-if="r.submission_status && r.submission_status !== 'none'" :class="['badge-sub', `sub-${r.submission_status}`]">
                {{ submissionLabel(r.submission_status) }}
              </span>
              <span v-else>—</span>
            </td>
            <td class="col-date">{{ formatDate(r.updated_at) }}</td>
            <td class="actions">
              <button @click="$emit('edit', r)" class="btn-action btn-edit">{{ $t('bo.common.edit') }}</button>
              <button v-if="r.status === 'draft'" @click="$emit('publish', r.id)" class="btn-action btn-publish">{{ $t('bo.resources.publish') }}</button>
              <button v-if="r.status !== 'archived'" @click="$emit('archive', r.id)" class="btn-action btn-archive">{{ $t('bo.resources.archive') }}</button>
              <button v-if="r.submission_status === 'pending_review'" @click="$emit('review', r.id)" class="btn-action btn-review">{{ $t('bo.resources.review') }}</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="pagination.last_page > 1" class="pagination">
        <button :disabled="pagination.current_page === 1" @click="loadPage(pagination.current_page - 1)" class="page-btn">{{ $t('bo.common.previous') }}</button>
        <span class="page-info">{{ $t('bo.translations.pageInfo', { current: pagination.current_page, last: pagination.last_page }) }}</span>
        <button :disabled="pagination.current_page === pagination.last_page" @click="loadPage(pagination.current_page + 1)" class="page-btn">{{ $t('bo.common.next') }}</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import adminResourceService from '@/services/adminResourceService'

const { t } = useI18n()

const emit = defineEmits(['edit', 'publish', 'archive', 'review'])

const TYPES = computed(() => [
  { value: 'article', label: t('bo.resources.types.article') },
  { value: 'video', label: t('bo.resources.types.video') },
  { value: 'document', label: t('bo.resources.types.document') },
  { value: 'template', label: t('bo.resources.types.template') },
  { value: 'tool', label: t('bo.resources.types.tool') },
  { value: 'slides', label: t('bo.resources.types.slides') },
  { value: 'link', label: t('bo.resources.types.link') },
  { value: 'course', label: t('bo.resources.types.course') },
  { value: 'other', label: t('bo.resources.types.other') },
])

const resources = ref([])
const loading = ref(false)
const error = ref('')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })
// Filtre availability laisse sur "toutes" par defaut pour ne pas masquer les
// ressources existantes (availability NULL). Le "generic par defaut" concerne
// le compte utilisateur (catalogue API), pas la liste admin.
const filters = reactive({ search: '', status: '', type: '', is_global: '', availability: '' })

let debounceTimer = null
const debouncedLoad = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => loadResources(), 300)
}

const loadResources = async (page = 1) => {
  loading.value = true
  error.value = ''
  try {
    const params = { page, ...filters }
    Object.keys(params).forEach(k => params[k] === '' && delete params[k])
    const res = await adminResourceService.getAll(params)
    resources.value = res.data?.data?.data || []
    const p = res.data?.data
    pagination.value = { current_page: p?.current_page || 1, last_page: p?.last_page || 1, total: p?.total || 0 }
  } catch {
    error.value = t('bo.resources.failedLoad')
  } finally {
    loading.value = false
  }
}

const loadPage = (page) => loadResources(page)

const typeLabel = (tp) => TYPES.value.find(x => x.value === tp)?.label || tp || '—'
const statusLabel = (s) => ({ draft: t('bo.resources.statusDraft'), published: t('bo.resources.statusPublished'), archived: t('bo.resources.statusArchived') }[s] || s)
const audienceLabel = (a) => ({ mentors: t('bo.resources.audienceMentors'), mentees: t('bo.resources.audienceMentees'), both: t('bo.resources.audienceBoth') }[a] || '—')
const availabilityLabel = (a) => ((a || 'generic') === 'program_specific' ? t('bo.resources.availabilityProgramSpecific') : t('bo.resources.availabilityGeneric'))
const submissionLabel = (s) => ({ pending_review: t('bo.resources.subPending'), approved: t('bo.resources.subApproved'), rejected: t('bo.resources.subRejected'), none: '—' }[s] || s)
const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

defineExpose({ loadResources })
onMounted(() => loadResources())
</script>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.input-search {
  flex: 1;
  min-width: 180px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.select-filter {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.state-msg {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.state-msg.error { color: #dc2626; }

.empty-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.resource-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.list-meta {
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.table tr:last-child td { border-bottom: none; }

.col-title { max-width: 240px; }
.col-date { white-space: nowrap; color: #6b7280; font-size: 0.8rem; }

.res-title { font-weight: 600; color: #1f2937; }
.res-company { font-size: 0.78rem; color: #9ca3af; margin-top: 0.15rem; }

.badge-type {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #f3f4f6;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: #374151;
  font-weight: 500;
  text-transform: capitalize;
}

.badge-status {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.st-draft { background: #fef3c7; color: #92400e; }
.st-published { background: #d1fae5; color: #065f46; }
.st-archived { background: #f3f4f6; color: #6b7280; }

.tag-global { display: inline-block; padding: 0.15rem 0.5rem; background: #dbeafe; color: #1e40af; border-radius: 0.25rem; font-size: 0.75rem; font-weight: 600; }
.tag-company { display: inline-block; padding: 0.15rem 0.5rem; background: #fce7f3; color: #9d174d; border-radius: 0.25rem; font-size: 0.75rem; font-weight: 600; }

.tag-availability { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 0.25rem; font-size: 0.75rem; font-weight: 600; }
.av-generic { background: #e0e7ff; color: #3730a3; }
.av-program { background: #ffedd5; color: #9a3412; }

.badge-sub {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.sub-pending_review { background: #fef9c3; color: #854d0e; }
.sub-approved { background: #d1fae5; color: #065f46; }
.sub-rejected { background: #fee2e2; color: #991b1b; }

.actions { white-space: nowrap; display: flex; gap: 0.4rem; flex-wrap: wrap; }

.btn-action {
  padding: 0.3rem 0.65rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
}

.btn-edit { background: #e0e7ff; color: #3730a3; }
.btn-edit:hover { background: #c7d2fe; }
.btn-publish { background: #d1fae5; color: #065f46; }
.btn-publish:hover { background: #a7f3d0; }
.btn-archive { background: #f3f4f6; color: #6b7280; }
.btn-archive:hover { background: #e5e7eb; }
.btn-review { background: #fef3c7; color: #92400e; }
.btn-review:hover { background: #fde68a; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:hover:not(:disabled) { background: #f9fafb; }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-info { font-size: 0.875rem; color: #6b7280; }
</style>
